import { memo } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { track } from "~/utils/track";
import { isMobileNative, supportsOrientationLock } from "~/utils/helpers";
import { logger } from "~/utils/logging";
import { requestOrientationLock } from "~/utils/events";
import { useUserContext } from "~/hooks/useUserContext";
import { WatchViewViewMode } from "~/components/watchView/WatchViewManager";
import { Video } from "~/components/video/Video";

import type { RefObject } from "react";
import type { VideoPlaylistEntry } from "~/components/video/Video";
import type { MediaPlayerContextValue } from "~/components/media/context";
import type { MediaPlayerFullscreenWindowPosition } from "~/components/media/types";
import type { SessionRouteLoaderData } from "./route";

export interface SessionVideoPlayerProps {
  playerContextRef: RefObject<MediaPlayerContextValue>;
  startTime: number;
  onEnded: () => void;
  onFullscreenSlidesChange?: (viewModeFullscreenSlides: boolean) => void;
  onFullscreenWindowPositionChange?: (
    position: MediaPlayerFullscreenWindowPosition,
  ) => void;
  onReplay: () => void;
  onRequestViewModeChange?: (viewMode: WatchViewViewMode) => void;
  onTimeUpdate: (timestamp: number) => void;
}

export const SessionVideoPlayer = memo(function SessionVideoPlayer({
  playerContextRef,
  startTime,
  onEnded,
  onFullscreenSlidesChange,
  onFullscreenWindowPositionChange,
  onReplay,
  onRequestViewModeChange,
  onTimeUpdate,
}: SessionVideoPlayerProps) {
  const { preferences, setPreference } = useUserContext();

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  function handleRateChange(playbackRate: number) {
    setPreference("playbackRate", playbackRate);
  }

  function handleVolumeChange(volume: number) {
    setPreference("volume", volume);
  }

  function handleCaptionChange(captionsEnabled: boolean) {
    setPreference("captionsEnabled", captionsEnabled);
  }

  function handleReplay() {
    onReplay?.();

    playerContextRef.current?.seek(0);

    track("crSessionRewatch", {
      className: data.session.class?.slug,
      sessionNumber: data.session.position,
      trigger: "sessionVideoPlayer",
    });
  }

  function handlePictureInPictureEnter() {
    onRequestViewModeChange?.(WatchViewViewMode.Freestyle);
  }

  function handlePictureInPictureLeave() {
    onRequestViewModeChange?.(WatchViewViewMode.Splitscreen);
  }

  function handleFullscreenEnter() {
    // Use the native fullscreen API on non-mobile-native platforms
    if (!isMobileNative()) {
      globalThis.document.body?.requestFullscreen().catch(logger.error);
    }

    if (supportsOrientationLock()) {
      requestOrientationLock("landscape");
    }

    onRequestViewModeChange?.(WatchViewViewMode.Fullscreen);
  }

  function handleFullscreenLeave() {
    if (globalThis.document.fullscreenElement) {
      globalThis.document.exitFullscreen().catch(logger.error);
    }

    if (supportsOrientationLock()) {
      requestOrientationLock("portrait");
    }

    onRequestViewModeChange?.(WatchViewViewMode.Splitscreen);
  }

  function handlePlaylistItem(item?: VideoPlaylistEntry) {
    const isLastItem =
      data.playlist.findIndex((x) => x.src === item?.src) ===
      data.playlist.length - 1;

    if (isLastItem) {
      onEnded?.();
    }
  }

  function handleAirplayingChange(isAirplaying: boolean) {
    if (isAirplaying) {
      track("crAirplaySession", {
        className: data.session.class?.slug,
        sessionNumber: data.session.position,
        trigger: "sessionVideoPlayer",
      });
    }
  }

  return (
    <Video
      autoplay
      captionsEnabled={preferences.captionsEnabled}
      chapters={data.chapters}
      contextRef={playerContextRef}
      playbackRate={preferences.playbackRate}
      volume={preferences.volume}
      playlist={data.playlist}
      startTime={startTime}
      title={data.session.title}
      onAirplayingChange={handleAirplayingChange}
      onCaptionChange={handleCaptionChange}
      onFullscreenEnter={handleFullscreenEnter}
      onFullscreenLeave={handleFullscreenLeave}
      onRateChange={handleRateChange}
      onVolumeChange={handleVolumeChange}
      onReplay={handleReplay}
      onTimeUpdate={onTimeUpdate}
      onPlaylistItem={handlePlaylistItem}
      onPictureInPictureEnter={handlePictureInPictureEnter}
      onPictureInPictureLeave={handlePictureInPictureLeave}
      onViewModeFullscreenSlidesChange={onFullscreenSlidesChange}
      onViewModeFullscreenWindowPositionChange={
        onFullscreenWindowPositionChange
      }
    />
  );
});
